import { uniq } from 'lodash';
import { AutomationActionType } from '../../../types/enums/AutomationActionType';
import { AutomationDeployAs } from '../../../types/enums/AutomationDeployAs';
import { AutomationType } from '../../../types/enums/AutomationType';
import { AutomationTypes } from '../../../types/enums/AutomationTypes';
import { FieldContext } from '../../../types/enums/FieldContext';
import { SelectorValueTypes } from '../../../types/enums/SelectorValueTypes';
import { DeployAsFlowOption } from '../../common/deploy-button/DeployButton';
import { ApiSweepFieldOperator } from '../../SweepFieldsOperatorSelector/sweepFieldsTypesAndOperators';
import { allowedOperatorsByType } from './flowAllowedOperatorsByType';
import { useSweepFieldsInfo } from '../../../sweep-fields/useSweepFieldsInfo';
import { SweepCriterion } from '../../../types/SweepCriterion';

export const allowedToDeployAsFlowAutomationTypes = [AutomationType.Default];

export const isDeployToFlowAllowed = (automationJson: DeepPartial<AutomationStructureNew>) => {
  return automationJson.type && allowedToDeployAsFlowAutomationTypes.includes(automationJson.type);
};

const allowedTriggerTypes = [
  AutomationTypes.OnCreate,
  AutomationTypes.OnUpdate,
  AutomationTypes.OnUpsert,
  AutomationTypes.FieldUpdate,
  AutomationTypes.StageEnter,
  AutomationTypes.StageExit,
];

const allowedActionTypes = [
  AutomationActionType.CreateRecord,
  AutomationActionType.UpdateRecords,
  AutomationActionType.PrioritizedActions,
];

export enum InvalidValidationReasons {
  InvalidTriggerType = 'InvalidTriggerType',
  InvalidDelay = 'InvalidDelay',
  InvalidPriorValue = 'InvalidPriorValue',
  InvalidOperator = 'InvalidOperator',
  InvalidActionType = 'InvalidActionType',
  InvalidNestedField = 'InvalidNestedField',
  IsNotLiteralValue = 'IsNotLiteralValue',
  InvalidActionFieldValueType = 'InvalidActionFieldValueType',
  InvalidRelationsRecordsToClone = 'InvalidRelationsRecordsToClone',
  InvalidBranchAction = 'InvalidBranchAction',
  InvalidCriteriaIsBranchActionEmpty = 'InvalidCriteriaIsBranchActionEmpty',
}
export const invalidValidationReasonsMap = {
  [InvalidValidationReasons.InvalidTriggerType]: 'Invalid trigger type',
  [InvalidValidationReasons.InvalidDelay]: 'Invalid delay',
  [InvalidValidationReasons.InvalidPriorValue]: 'Invalid prior value',
  [InvalidValidationReasons.InvalidOperator]: 'Invalid operator',
  [InvalidValidationReasons.InvalidActionType]: 'Invalid action type',
  [InvalidValidationReasons.InvalidNestedField]: 'Invalid nested field',
  [InvalidValidationReasons.IsNotLiteralValue]: 'Is not literal value',
  [InvalidValidationReasons.InvalidActionFieldValueType]: 'Invalid action field value type',
  [InvalidValidationReasons.InvalidRelationsRecordsToClone]: 'Invalid relations records to clone',
  [InvalidValidationReasons.InvalidBranchAction]: 'Invalid branch action',
  [InvalidValidationReasons.InvalidCriteriaIsBranchActionEmpty]:
    'Invalid criteria is empty in branch action',
};

const allowedActionFieldValueTypes = [
  SelectorValueTypes.LITERAL,
  SelectorValueTypes.REFERENCE,
  SelectorValueTypes.PRIOR,
];

const useIsDeployToFlowValid = () => {
  const { getSalesforceDisplayTypeFromFieldId } = useSweepFieldsInfo();
  const validateCriteria = (criteria: SweepCriterion[], criteriaIsMandatory?: boolean) => {
    const invalidValidationReasons: InvalidValidationReasons[] = [];

    if (criteriaIsMandatory && !criteria.length) {
      invalidValidationReasons.push(InvalidValidationReasons.InvalidCriteriaIsBranchActionEmpty);
    }

    const hasInvalidOperator = criteria.some((c) => {
      const sfDisplayType = getSalesforceDisplayTypeFromFieldId(c._fieldIds);
      const allowedOperators =
        allowedOperatorsByType[sfDisplayType as keyof typeof allowedOperatorsByType];
      return !allowedOperators?.includes(c.operator as ApiSweepFieldOperator);
    });

    if (hasInvalidOperator) {
      invalidValidationReasons.push(InvalidValidationReasons.InvalidOperator);
    }

    const invalidNestedField = criteria.some((c) => c._fieldIds.length > 1);
    if (invalidNestedField) {
      invalidValidationReasons.push(InvalidValidationReasons.InvalidNestedField);
    }

    const isNotLiteralValue = criteria.some((c) => c.valueType !== SelectorValueTypes.LITERAL);
    if (isNotLiteralValue) {
      invalidValidationReasons.push(InvalidValidationReasons.IsNotLiteralValue);
    }

    const hasPriorValue = criteria.some((c) => c.fieldContext === FieldContext.PRIOR_VALUE);
    if (hasPriorValue) {
      invalidValidationReasons.push(InvalidValidationReasons.InvalidPriorValue);
    }

    return invalidValidationReasons;
  };

  const validateActions = (actions: (DeepPartial<AutomationAction> | undefined)[]) => {
    const invalidValidationReasons: InvalidValidationReasons[] = [];

    const hasInvalidActionTypes = actions.some(
      (a) => !allowedActionTypes.includes(a?.actionType as AutomationActionType),
    );
    if (hasInvalidActionTypes) {
      invalidValidationReasons.push(InvalidValidationReasons.InvalidActionType);
    }

    const createRecordActions = (actions?.filter(
      (a) => a?.actionType === AutomationActionType.CreateRecord,
    ) ?? []) as UpdateOrCreateAutomationAction[];

    const updateRecordActions = (actions?.filter(
      (a) => a?.actionType === AutomationActionType.UpdateRecords,
    ) ?? []) as UpdateOrCreateAutomationAction[];

    const createOrUpdateRecordActions = [...createRecordActions, ...updateRecordActions];

    const hasInvalidActionCreateRecordFieldNestedField = createOrUpdateRecordActions.some(
      (action) => {
        return action.actionParams?._fields?.some((f) => f._fieldIds.length > 1);
      },
    );

    if (hasInvalidActionCreateRecordFieldNestedField) {
      invalidValidationReasons.push(InvalidValidationReasons.InvalidNestedField);
    }

    const hasInvalidActionFieldValueType = createOrUpdateRecordActions.some((action) => {
      return action?.actionParams?._fields?.some(
        (f) => !allowedActionFieldValueTypes.includes(f.valueType as SelectorValueTypes),
      );
    });

    if (hasInvalidActionFieldValueType) {
      invalidValidationReasons.push(InvalidValidationReasons.InvalidActionFieldValueType);
    }

    const hasInvalidRelationsRecordsToClone = createRecordActions.some((action) => {
      return Boolean(action?.actionParams?.relationsRecordsToClone?.length);
    });

    if (hasInvalidRelationsRecordsToClone) {
      invalidValidationReasons.push(InvalidValidationReasons.InvalidRelationsRecordsToClone);
    }

    const hasInvalidActionUpdateRecordFieldNestedField = updateRecordActions
      .filter((a) => a.actionParams?.thisRecord)
      .some((action) => {
        return action?.actionParams?._fields?.some((f) => f._fieldIds.length > 1);
      });

    if (hasInvalidActionUpdateRecordFieldNestedField) {
      invalidValidationReasons.push(InvalidValidationReasons.InvalidNestedField);
    }

    updateRecordActions.forEach((action) => {
      const validation = validateCriteria(action?.actionParams?.criteria?.criteria ?? []).filter(
        (v) => v !== InvalidValidationReasons.IsNotLiteralValue,
      ); //allow reference to current and old triggering

      if (validation.length > 0) {
        invalidValidationReasons.push(...validation);
      }
    });

    return invalidValidationReasons;
  };

  const isDeployToFlowValid = (automationJson: DeepPartial<AutomationStructureNew>) => {
    const invalidValidationReasons: InvalidValidationReasons[] = [];

    const ad = automationJson.automationDetails;

    if (!ad) {
      return {
        invalidValidationReasons: [InvalidValidationReasons.InvalidTriggerType],
        isValid: false,
      };
    }

    if (!ad.triggerType || !allowedTriggerTypes.includes(ad.triggerType)) {
      invalidValidationReasons.push(InvalidValidationReasons.InvalidTriggerType);
    }

    if (ad.delay) {
      invalidValidationReasons.push(InvalidValidationReasons.InvalidDelay);
    }

    if (ad.when?.criteria?.criteria) {
      invalidValidationReasons.push(...validateCriteria(ad.when.criteria.criteria));
    }

    if (ad.actions) {
      const invalidValidationReasonsActions = validateActions(ad.actions);
      if (invalidValidationReasonsActions.length > 0) {
        invalidValidationReasons.push(...invalidValidationReasonsActions);
      }
    }

    const prioritizedActions = (ad.actions?.filter(
      (a) => a?.actionType === AutomationActionType.PrioritizedActions,
    ) ?? []) as ConditionalActionsAutomationAction[];

    if (prioritizedActions.length > 0) {
      prioritizedActions.forEach((action) => {
        action?.actionParams?.prioritizedActions?.forEach((pa) => {
          const _validateActions = validateActions(pa.actions);
          if (_validateActions.length > 0) {
            invalidValidationReasons.push(InvalidValidationReasons.InvalidBranchAction);
            invalidValidationReasons.push(..._validateActions);
          }

          const _validateCriteria = validateCriteria(pa.when?.criteria?.criteria ?? [], true);
          if (_validateCriteria.length > 0) {
            invalidValidationReasons.push(InvalidValidationReasons.InvalidBranchAction);
            invalidValidationReasons.push(..._validateCriteria);
          }
        });
      });
    }

    const uniqueInvalidValidationReasons = uniq(invalidValidationReasons);

    return {
      invalidValidationReasons: uniqueInvalidValidationReasons,
      isValid: uniqueInvalidValidationReasons.length === 0,
    };
  };

  return { isDeployToFlowValid };
};

export const useGetDeployButtonOptions = () => {
  const { isDeployToFlowValid } = useIsDeployToFlowValid();
  const getDeployButtonOptions = (
    automationJson: DeepPartial<AutomationStructureNew>,
    isDeployToSfValid: boolean,
  ): {
    defaultDeployType: AutomationDeployAs;
    deployAsFlowOption?: DeployAsFlowOption;
    isDeployToFlowValid?: boolean;
    invalidValidationReasons: InvalidValidationReasons[];
  } => {
    const { invalidValidationReasons, isValid: isDeployAsFlowValid } =
      isDeployToFlowValid(automationJson);
    const _isDeployToFlowValid = isDeployToSfValid && isDeployAsFlowValid;

    if (automationJson.type && allowedToDeployAsFlowAutomationTypes.includes(automationJson.type)) {
      if (!automationJson.deployedAs) {
        if (isDeployToFlowAllowed(automationJson)) {
          return {
            defaultDeployType: AutomationDeployAs.AUTOMATION,
            deployAsFlowOption: {
              display: true,
              disabled: !_isDeployToFlowValid,
            },
            isDeployToFlowValid: _isDeployToFlowValid,
            invalidValidationReasons: invalidValidationReasons,
          };
        } else {
          return {
            defaultDeployType: AutomationDeployAs.AUTOMATION,
            deployAsFlowOption: undefined,
            isDeployToFlowValid: false,
            invalidValidationReasons: invalidValidationReasons,
          };
        }
      }

      if (automationJson.deployedAs === AutomationDeployAs.FLOW) {
        return {
          defaultDeployType: AutomationDeployAs.FLOW,
          deployAsFlowOption: undefined,
          isDeployToFlowValid: _isDeployToFlowValid,
          invalidValidationReasons: invalidValidationReasons,
        };
      }
      if (automationJson.deployedAs === AutomationDeployAs.AUTOMATION) {
        return {
          defaultDeployType: AutomationDeployAs.AUTOMATION,
          deployAsFlowOption: undefined,
          invalidValidationReasons: invalidValidationReasons,
        };
      }
    }
    return {
      defaultDeployType: AutomationDeployAs.AUTOMATION,
      isDeployToFlowValid: false,
      invalidValidationReasons: invalidValidationReasons,
    };
  };
  return { getDeployButtonOptions };
};
